import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { toast } from '@/components/ui/sonner'
import type { DataLabel, LakeDocument } from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'
import { LakeFileDetail } from './LakeFileDetail'

interface LakeDetailSheetProps {
  docId: string | null
  open: boolean
  onClose: () => void
  labels: DataLabel[]
  onDocumentUpdate?: (doc: LakeDocument) => void
  onDocumentDeleted?: (docId: string) => void
  /** Fired after a successful reprocess so the parent can invalidate
      the folder tree / list view. */
  onReprocessed?: () => void
  /** Import-to-case mode: when set, the header exposes an "Import to case"
      button and the modal is pre-selected to this case. */
  destinationCaseId?: string | null
  destinationCaseName?: string | null
  onImportSucceeded?: () => void
}

const VALID_TABS = ['analysis', 'indexed-data', 'imports', 'processing-stats'] as const
type DetailTab = (typeof VALID_TABS)[number]

export function LakeDetailSheet({
  docId,
  open,
  onClose,
  labels,
  onDocumentUpdate,
  onDocumentDeleted,
  onReprocessed,
  destinationCaseId,
  destinationCaseName,
  onImportSucceeded,
}: LakeDetailSheetProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [document, setDocument] = useState<LakeDocument | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Sync ?tab= URL param to detail sub-tab
  const tabParam = searchParams.get('tab') as DetailTab | null
  const activeTab: DetailTab =
    tabParam && VALID_TABS.includes(tabParam as DetailTab) ? (tabParam as DetailTab) : 'analysis'

  const handleTabChange = (tab: DetailTab) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('tab', tab)
        return next
      },
      { replace: true }
    )
  }

  // Fetch document whenever docId changes (and sheet is open)
  useEffect(() => {
    if (!docId || !open) {
      setDocument(null)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    lakeApi
      .getDocument(docId)
      .then(doc => setDocument(doc))
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load document'))
      .finally(() => setLoading(false))
  }, [docId, open])

  const handleDocumentUpdate = (doc: LakeDocument) => {
    setDocument(doc)
    onDocumentUpdate?.(doc)
  }

  const label = document ? labels.find(l => l.id === document.label_id) : null

  return (
    <Sheet
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose()
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:min-w-[65vw] p-0 overflow-hidden flex flex-col"
        showCloseButton={true}
        style={{ background: 'var(--background)', color: 'var(--foreground)' }}
      >
        {loading && (
          <div className="flex items-center justify-center flex-1 h-full">
            <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
          </div>
        )}

        {error && !loading && (
          <div className="flex items-center justify-center flex-1 h-full text-sm text-destructive px-6">
            {error}
          </div>
        )}

        {document && !loading && !error && (
          <div className="flex-1 overflow-y-auto">
            <LakeFileDetail
              document={document}
              labelName={label?.name ?? 'Unknown'}
              labelColor={label?.color ?? null}
              onDocumentUpdate={handleDocumentUpdate}
              onReprocessed={() => {
                // Refresh the parent list once and refetch the open document.
                // Ongoing updates are handled elsewhere: the page polls the
                // list (silently) while any document is processing, and
                // `useLakeDocumentStatus` inside LakeFileDetail keeps THIS
                // document fresh until it reaches a terminal state.
                //
                // Do NOT start an interval here. A previous version ran an
                // uncancelled 30s `setInterval` that captured `docId` in its
                // closure and kept calling `setDocument` — so opening a
                // different document mid-reprocess overwrote the sheet with the
                // stale (still-processing) document, and its repeated
                // non-silent list refreshes made the list flicker.
                if (!docId) return
                onReprocessed?.()
                lakeApi
                  .getDocument(docId)
                  .then(setDocument)
                  .catch(() => {})
              }}
              onDelete={async () => {
                await lakeApi.deleteDocument(document.id)
                toast.success(`Deleted ${document.filename}`)
                onDocumentDeleted?.(document.id)
                onClose()
              }}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              destinationCaseId={destinationCaseId}
              destinationCaseName={destinationCaseName}
              onImportSucceeded={onImportSucceeded}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
